// Graph API
import type { PassageSpeakerDto } from '@/features/humint/recording/passage-speakers'
import { apiClient } from './client'

export interface Person {
  id: string
  name: string
  labels: string[]
  type?: string
  properties?: Record<string, unknown>
  /**
   * On the read of one `TranscriptPassage` node only (`getNode`): who each voice of the passage
   * was said to be on the recording's page. A voice nobody named is not in it; absent for any
   * other node, and from a server that does not name voices yet.
   */
  speakers?: PassageSpeakerDto[]
}

export interface Relationship {
  id: string
  source: string
  target: string
  type: string
  properties?: Record<string, unknown>
}

export interface GraphData {
  nodes: Person[]
  relationships: Relationship[]
}

export interface AllDataNode extends Person {
  degree?: number
  source_count?: number
  investigation_score?: number | null
  score_components?: string | null
  is_suspect?: boolean
}

export interface Entity {
  id: string
  name: string
  type: string
}

export interface PathResult {
  found: boolean
  path_length: number
  total_cost?: number
  nodes: Person[]
  relationships: Relationship[]
  node_ids: string[]
  relationship_ids: string[]
}

export interface Text2CypherRequest {
  query: string
}

export interface Text2CypherResponse {
  answer: string
  generated_cypher?: string
  context?: unknown
}

export interface EntityMatchRequest {
  query: string
  max_matches?: number
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>
}

export interface EntityMatch {
  node_id: string
  node_name: string
  confidence: number
  reasoning: string
}

export interface EntityMatchResponse {
  matches: EntityMatch[]
}

export interface AllPathsRequest {
  source_node_id: string
  target_node_id: string
  max_extra_hops: number
}

export interface ShortestPathRequest {
  source_node_id: string
  target_node_id: string
  max_depth: number
}

export interface UpdateNodePropertiesRequest {
  properties?: Record<string, unknown>
  remove_properties?: string[]
}

export interface CreateNoteRequest {
  text: string
  isSticky: boolean
  x?: number
  y?: number
  connected_node_ids?: string[]
}

export interface CreateInvestigationNoteEdge {
  id: string
  type: string
  source: string
  target: string
  properties: Record<string, unknown>
}

export interface CreateInvestigationNoteResponse {
  node_id: string
  element_id: string
  labels: string[]
  name: string
  properties: Record<string, unknown>
  edges: CreateInvestigationNoteEdge[]
}

export interface UpdateNoteRequest {
  text: string
  isSticky: boolean
}

export interface NodeGroup {
  id: string
  name: string
  description: string
  color: string
  node_ids: string[]
}

export interface CreateGroupRequest {
  name: string
  description: string
  color: string
  node_ids: string[]
}

export interface GraphLayout {
  node_positions: Record<string, { x: number; y: number }>
  expanded_nodes: string[]
  // Free-form per-case UI state — selected layout, viewport, pinned ids, etc.
  // Backend stores it as JSONB and passes it through opaquely.
  ui_state?: Record<string, unknown> | null
}

// Notes & Findings from Graph
export interface GraphNoteItem {
  id: string
  type: 'note'
  text: string
  name?: string | null
  is_sticky: boolean
  color?: string | null
  author_name?: string | null
  author_id?: string | null
  created_at: string
  updated_at?: string | null
  documentation: string[]
  connected_node_ids: string[]
}

export interface GraphFindingItem {
  id: string
  type: 'finding'
  name?: string | null
  description: string
  finding_timestamp: string
  author_name: string
  author_id?: string | null
  created_at: string
  updated_at: string
  documentation: string[]
  connected_node_ids: string[]
}

export type GraphNoteOrFinding = GraphNoteItem | GraphFindingItem

export interface NotesAndFindingsResponse {
  items: GraphNoteOrFinding[]
  total_count: number
}

export interface ExpandNodeResponse {
  nodes: Person[]
  relationships: Relationship[]
  parentId: string
  childCount: number
}

export interface CollapseNodeResponse {
  nodesToHide: string[]
  aggregatedEdges: Relationship[]
  parentId: string
  childCount: number
}

export interface StaticGeoEntity {
  entity_id: string
  entity_key: string
  entity_type: string
  entity_name: string
  latitude: number
  longitude: number
  location_name: string | null
  /** Optional ISO-8601 timestamp the entity is "anchored" to. Set for
   *  point-in-time entities like SocialMediaPost; null for pure static
   *  infrastructure (BTSStation, Person address, …). Currently unused by
   *  the map (static pins render at full opacity regardless of playhead)
   *  but kept in the payload for future temporal overlays. */
  anchor_time?: string | null
  document_ids: string[]
  attributes?: Record<string, unknown>
}

export interface TrackableEntity {
  entity_id: string
  entity_key: string
  entity_type: string
  entity_name: string | null
  observation_count: number
  time_range_start: string
  time_range_end: string
  document_ids: string[]
}

// --- Graph Analysis types ---

export interface ComponentEntity {
  id: string
  name: string
  type: string
  score: number | null
  betweenness: number | null
  is_bridge: boolean | null
}

export interface GraphComponent {
  comp_id: number
  size: number
  entity_types: string[]
  sample_entities: ComponentEntity[]
  /** Count of relationships whose both endpoints are in this component. */
  relationship_count?: number
}

export interface ComponentsResponse {
  components: GraphComponent[]
  computed_at: string | null
  stale: boolean
}

export interface BridgeInsight {
  entity_a_id: string
  entity_a_name: string
  entity_a_type: string
  component_a: number
  entity_b_id: string
  entity_b_name: string
  entity_b_type: string
  component_b: number
  document_title?: string
  document_id?: string
}

export interface TopEntityEntry {
  id: string
  name: string
  score: number | null
  degree: number | null
  source_count: number | null
  betweenness: number | null
  is_suspect: boolean | null
  is_bridge: boolean | null
  rel_diversity: number | null
  component_id: number | null
}

export interface TopEntitiesResponse {
  entity_types: Array<{
    entity_type: string
    total_count: number
    top_entities: TopEntityEntry[]
  }>
}

export interface AnalysisResult {
  total_scored: number
  component_count: number
  bridge_count: number
  node_count: number
}

export interface PatternQueryRequest {
  origin?: { entity_type: string; search_term?: string; element_id?: string } | null
  search_term?: string | null
  chain?: Array<{
    relationship_type?: string | null
    target: { entity_type: string; search_term?: string; element_id?: string }
  }>
  component_id?: number | null
  limit?: number
}

export interface RelationshipTypeCount {
  rel_type: string
  count: number
}

export const graphApi = {
  // Get main graph for case
  getGraph: (caseId: string, limit = 500, minimal = true, onlyPinned = false) =>
    apiClient.get<GraphData>(`/cases/${caseId}/graph`, {
      params: { limit, minimal, only_pinned: onlyPinned },
    }),

  // Clear entire graph for case
  clearGraph: (caseId: string) => apiClient.delete<void>(`/cases/${caseId}/graph`),

  // Get all entities for case
  getEntities: (caseId: string) =>
    apiClient.get<{ entities: Entity[] }>(`/cases/${caseId}/graph/entities`),

  // Knowledge graph view - simple graph with optional entity type filter
  getKnowledgeGraph: (
    caseId: string,
    options?: { limit?: number; entityTypes?: string[]; componentId?: number }
  ) =>
    apiClient.get<{
      nodes: Person[]
      relationships: Relationship[]
      total: number
    }>(`/cases/${caseId}/graph/knowledge-graph`, {
      params: {
        limit: options?.limit ?? 1000,
        ...(options?.entityTypes && options.entityTypes.length > 0
          ? { entity_types: options.entityTypes.join(',') }
          : {}),
        ...(options?.componentId != null ? { component_id: options.componentId } : {}),
      },
    }),

  // Compute investigation scores for all entities
  computeScores: (caseId: string, weights?: Record<string, number>) =>
    apiClient.post<{
      total_scored: number
      top_entities: Array<{
        node_id: string
        name: string
        labels: string[]
        score: number
        components: Record<string, number>
        degree: number
        source_count: number
      }>
    }>(`/cases/${caseId}/graph/compute-scores`, weights ? { weights } : {}),

  // Paginated listing of all entities for All Data view
  getAllData: (
    caseId: string,
    options?: {
      skip?: number
      limit?: number
      search?: string
      entityType?: string
      sortField?: string
      sortDir?: string
      isSuspect?: boolean | null
      minConnections?: number
      documentId?: string
      nodeIds?: string[]
    }
  ) =>
    apiClient.get<{
      nodes: AllDataNode[]
      total: number
      skip: number
      limit: number
      entity_types: Array<{ label: string; count: number }>
    }>(`/cases/${caseId}/graph/all-data`, {
      params: {
        skip: options?.skip ?? 0,
        limit: options?.limit ?? 50,
        search: options?.search || '',
        entity_type: options?.entityType || '',
        sort_field: options?.sortField || 'type',
        sort_dir: options?.sortDir || 'asc',
        ...(options?.isSuspect != null ? { is_suspect: options.isSuspect } : {}),
        ...(options?.minConnections ? { min_connections: options.minConnections } : {}),
        ...(options?.documentId ? { document_id: options.documentId } : {}),
        ...(options?.nodeIds && options.nodeIds.length > 0
          ? { node_ids: options.nodeIds.join(',') }
          : {}),
      },
    }),

  // Search entities/relationships/chunks
  searchEntities: (
    caseId: string,
    query: string,
    options?: {
      limit?: number
      entityType?: string
      searchType?: 'entities' | 'relationships' | 'chunks'
    }
  ) =>
    apiClient.get<{
      results: Array<{
        id: string
        name?: string
        type: string
        labels?: string[]
        properties?: Record<string, unknown>
        result_type: 'entity' | 'relationship' | 'chunk'
        // For relationships
        source?: string
        target?: string
        source_name?: string
        target_name?: string
      }>
    }>(`/cases/${caseId}/graph/search`, {
      params: {
        q: query,
        limit: options?.limit ?? 50,
        entity_type: options?.entityType,
        search_type: options?.searchType ?? 'entities',
      },
    }),

  // Get neighborhood around node
  getNeighborhood: (caseId: string, nodeId: string, hops = 2) =>
    apiClient.get<GraphData>(`/cases/${caseId}/graph/neighborhood`, {
      params: { nodeId, hops },
    }),

  // Get neighbors of a specific node
  getNeighbors: (caseId: string, nodeId: string, hops = 2) =>
    apiClient.get<GraphData>(`/cases/${caseId}/graph/neighbors/${nodeId}`, {
      params: { hops },
    }),

  // Text to Cypher query
  text2cypher: (caseId: string, request: Text2CypherRequest) =>
    apiClient.post<Text2CypherResponse>(`/cases/${caseId}/graph/text2cypher`, request),

  // Match entities using LLM agent (handles diacritics, titles, partial names, typos)
  matchEntities: (caseId: string, request: EntityMatchRequest) =>
    apiClient.post<EntityMatchResponse>(`/cases/${caseId}/graph/match-entities`, request),

  // Find all paths between two nodes
  findAllPaths: (caseId: string, request: AllPathsRequest) =>
    apiClient.post<unknown>(`/cases/${caseId}/graph/all-paths`, request),

  // Find shortest path
  findShortestPath: (caseId: string, request: ShortestPathRequest) =>
    apiClient.post<PathResult>(`/cases/${caseId}/graph/shortest-path`, request),

  // Hydrate a set of node + relationship element IDs into full nodes/edges.
  // Used by agent tools that return only IDs to keep their SSE payload small.
  hydrateGraph: (caseId: string, request: { node_ids: string[]; relationship_ids: string[] }) =>
    apiClient.post<{ nodes: Person[]; relationships: Relationship[] }>(
      `/cases/${caseId}/graph/hydrate`,
      request
    ),

  // Deduplicate relationships
  deduplicateRelationships: (caseId: string) =>
    apiClient.post<{ deleted_count: number }>(`/cases/${caseId}/graph/deduplicate-relationships`),

  // Fetch the K-hop neighborhood of a node from Neo4j (default K=1).
  getFaceEvidence: (caseId: string, personId: string, faceId: string) =>
    apiClient.get<GraphData>(
      `/v1/cases/${caseId}/graph/identities/${encodeURIComponent(personId)}/faces/${encodeURIComponent(faceId)}`
    ),

  getNodeNeighbors: (caseId: string, nodeId: string, opts?: { hops?: number }) =>
    apiClient.get<{ nodes: Person[]; relationships: Relationship[] }>(
      `/v1/cases/${caseId}/graph/nodes/${encodeURIComponent(nodeId)}/neighbors${
        opts?.hops && opts.hops !== 1 ? `?hops=${opts.hops}` : ''
      }`
    ),

  // For each visible node, return counts of its 1-hop neighbours that are
  // NOT in `visibleNodeIds`, grouped by entity label. Drives the "+N"
  // expansion-hint badge in the investigation graph view.
  getNeighborCounts: (caseId: string, visibleNodeIds: string[]) =>
    apiClient.post<{ counts: Record<string, Record<string, number>> }>(
      `/cases/${caseId}/graph/neighbor-counts`,
      { visible_node_ids: visibleNodeIds }
    ),

  // Get node details
  getNode: (caseId: string, nodeId: string) =>
    apiClient.get<Person>(`/cases/${caseId}/graph/node/${encodeURIComponent(nodeId)}`),

  // Delete node
  deleteNode: (caseId: string, nodeId: string) =>
    apiClient.delete<void>(`/cases/${caseId}/graph/node/${encodeURIComponent(nodeId)}`),

  // Bulk delete nodes
  bulkDeleteNodes: (caseId: string, nodeIds: string[]) =>
    apiClient.post<{ deleted: number }>(`/cases/${caseId}/graph/bulk-delete-nodes`, {
      node_ids: nodeIds,
    }),

  // Update node properties
  updateNodeProperties: (caseId: string, nodeId: string, request: UpdateNodePropertiesRequest) =>
    apiClient.patch<Person>(`/cases/${caseId}/graph/node/${encodeURIComponent(nodeId)}`, request),

  // Delete relationship
  deleteRelationship: (caseId: string, relationshipId: string) =>
    apiClient.delete<void>(
      `/cases/${caseId}/graph/relationship/${encodeURIComponent(relationshipId)}`
    ),

  // Batch delete nodes
  batchDeleteNodes: (caseId: string, nodeIds: string[]) =>
    apiClient.post<void>(`/cases/${caseId}/graph/nodes/batch-delete`, { node_ids: nodeIds }),

  // Get daily summary events
  getDailySummaryEvents: (caseId: string, nodeId: string) =>
    apiClient.get<GraphData>(`/cases/${caseId}/graph/daily-summary-events`, {
      params: { nodeId },
    }),

  // Expand node (progressive expansion for nodes with >30 children)
  expandNode: (caseId: string, nodeId: string, visibleNodeIds: string[] = []) =>
    apiClient.get<ExpandNodeResponse>(`/cases/${caseId}/graph/expand-node`, {
      params: {
        nodeId,
        visibleNodeIds: visibleNodeIds.join(','),
      },
    }),

  // Collapse node (hide all descendants)
  collapseNode: (caseId: string, nodeId: string, visibleNodeIds: string[]) =>
    apiClient.get<CollapseNodeResponse>(`/cases/${caseId}/graph/collapse-node`, {
      params: {
        nodeId,
        visibleNodeIds: visibleNodeIds.join(','),
      },
    }),

  // Investigation notes
  createNote: (caseId: string, request: CreateNoteRequest) =>
    apiClient.post<CreateInvestigationNoteResponse>(`/cases/${caseId}/investigation/note`, request),

  updateNote: (caseId: string, noteId: string, request: UpdateNoteRequest) =>
    apiClient.put<Entity>(`/cases/${caseId}/investigation/note/${noteId}`, request),

  // Node groups
  getGroups: (caseId: string) => apiClient.get<NodeGroup[]>(`/cases/${caseId}/graph/groups`),

  createGroup: (caseId: string, request: CreateGroupRequest) =>
    apiClient.post<NodeGroup>(`/cases/${caseId}/graph/groups`, request),

  deleteGroup: (caseId: string, groupId: string, deleteNodes: boolean = false) =>
    apiClient.delete<{ deleted_nodes?: number }>(
      `/cases/${caseId}/graph/groups/${encodeURIComponent(groupId)}`,
      { params: { delete_nodes: deleteNodes } }
    ),

  // Graph layout persistence
  getLayout: (caseId: string, graphType = '2d') =>
    apiClient.get<GraphLayout>(`/cases/${caseId}/graph-layout`, {
      params: { graphType },
    }),

  saveLayout: (caseId: string, layout: GraphLayout, graphType = '2d') =>
    apiClient.put<void>(`/cases/${caseId}/graph-layout`, {
      graph_type: graphType,
      ...layout,
    }),

  // Notes and Findings from graph (for investigation panel)
  getNotesAndFindings: (caseId: string) =>
    apiClient.get<NotesAndFindingsResponse>(`/cases/${caseId}/graph/notes-and-findings`),

  // Geo / track endpoints (for Cesium animation)
  getTrackableEntities: (caseId: string) =>
    apiClient.get<TrackableEntity[]>(`/cases/${caseId}/graph/trackable-entities`),

  getStaticGeoEntities: (caseId: string) =>
    apiClient.get<StaticGeoEntity[]>(`/cases/${caseId}/graph/static-geo-entities`),

  // --- Graph Analysis ---

  computeAnalysis: (caseId: string, weights?: Record<string, number>) =>
    apiClient.post<AnalysisResult>(`/cases/${caseId}/graph/compute-analysis`, { weights }),

  getComponents: (caseId: string) =>
    apiClient.get<ComponentsResponse>(`/cases/${caseId}/graph/components`),

  getBridges: (caseId: string) =>
    apiClient.get<{ bridges: BridgeInsight[] }>(`/cases/${caseId}/graph/bridges`),

  getTopEntities: (caseId: string, perTypeLimit = 10) =>
    apiClient.get<TopEntitiesResponse>(`/cases/${caseId}/graph/top-entities`, {
      params: { per_type_limit: perTypeLimit },
    }),

  // Pattern query - structured graph search
  patternQuery: (caseId: string, body: PatternQueryRequest) =>
    apiClient.post<{ nodes: Person[]; relationships: Relationship[]; total: number }>(
      `/cases/${caseId}/graph/pattern-query`,
      body
    ),

  // Fetch all relationships whose both endpoints are in the given node ID set.
  // Used to complete cross-type edges after unioning multiple pattern queries
  // (Bloom-style scene semantics).
  getEdgesBetween: (caseId: string, nodeIds: string[]) =>
    apiClient.post<{ relationships: Relationship[] }>(`/cases/${caseId}/graph/edges-between`, {
      node_ids: nodeIds,
    }),

  // Get distinct relationship types for a case
  getRelationshipTypes: (caseId: string) =>
    apiClient.get<RelationshipTypeCount[]>(`/cases/${caseId}/graph/relationship-types`),
}
